<template>
  <div class="modal" :class="{ 'modal--no-padding': !showBackground }">
    <div v-if="showBackground" class="modal__background" @click="close" :style="modalStyles"></div>
    <div class="modal__content" :class="{ 'modal__content--centered': centered }" :style="contentWidth">
      <button v-if="!hideCloseButton" class="modal__button" @click="close">
        <div class="is-sr-only">Close</div>
        <img :src="closeIconSrc" alt="Close icon" />
      </button>
      <slot name="header"></slot>
      <slot name="body"></slot>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
// Vue1 `require('...svg')` -> ES-module import so Vite resolves the asset
// (inlined as a data URI under the build's assetsInlineLimit).
import closeSvg from '../../assets/v3-assets/close.svg';

export default {
  name: 'modal',
  // Vue3 (LP-4/MR-78 emits parity): declare `close` so parent `@close` listeners
  // bind as a component event, not native-attr fallthrough.
  emits: ['close'],
  props: {
    backgroundColor: {
      type: String,
      default() {
        return '#e5e5e5';
      },
    },
    centered: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideCloseButton: Boolean,
    width: Number,
    showBackground: {
      type: Boolean,
      default() {
        return true;
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleEscapeKeypress);
    this.trap();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEscapeKeypress);
  },
  data() {
    return {
      escapeKeyCode: 27,
      elements: [],
      focusableElements: [
        'a[href]:not([disabled])',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input[type="text"]:not([disabled])',
        'input[type="radio"]:not([disabled])',
        'input[type="checkbox"]:not([disabled])',
        'input[type="search"]:not([disabled])',
        'select:not([disabled])',
      ],
      keycode: 9, // tab key
    };
  },
  computed: {
    closeIconSrc() {
      return closeSvg;
    },

    modalStyles() {
      return {
        backgroundColor: this.backgroundColor,
      };
    },

    contentWidth() {
      if (!this.width) return null;

      return {
        minWidth: `${this.width}px`,
      };
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },

    handleEscapeKeypress(event) {
      const isEscapeKey = event.key === 'Escape' || event.keyCode === this.escapeKeyCode;
      if (!isEscapeKey) {
        return false;
      }

      this.close();

      return true;
    },

    trap() {
      const el = this.$el;
      this.elements = el.querySelectorAll([...this.focusableElements].join(', '));
      setTimeout(() => {
        if (this.elements[0]) this.elements[0].focus(); // Init focus to first element
      }, 0);
      el.addEventListener('keydown', this.handleKeydown);
    },

    release() {
      this.elements = [];
    },

    handleKeydown(event) {
      const isTabKey = event.key === 'Tab' || event.keycode === this.keycode;
      if (!isTabKey) {
        return false;
      }

      const firstFocusableElement = this.elements[0];
      const lastFocusableElement = this.elements[this.elements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          this.setFocus(lastFocusableElement);
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          this.setFocus(firstFocusableElement);
        }
      }

      return true;
    },

    /**
     * Sets the focus to a given element or first focusable child
     *
     * @param {DOMNode} element the element to focus
     * @return {void}
     */
    setFocus(element) {
      if (element) element.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/_helpers';

.modal {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: 1rem;
  z-index: 9;
  &--no-padding {
    padding: 0;
  }
  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #e5e5e5;
  }
  &__button {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
  &__content {
    width: 100%;
    max-width: 100%;
    height: 100%;
    position: relative;
    background: #fff;
    border-radius: 0.5rem;
    padding: 1rem;
    line-height: 1.5;
    overflow-y: auto;
    &--centered {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: auto;
      width: auto;
    }
  }
}
</style>
